import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, EmptyState, LoadingState, KpiCard, StatusBadge } from "@/command/ccUi";

const money = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);

const CONFIG = {
  invoices: {
    label: "Invoices", entity: "invoice",
    columns: [["number", "Number"], ["client", "Client"], ["amount", "Amount", "money"], ["status", "Status", "badge"], ["due_date", "Due"]],
    fields: [["number", "Invoice number", "text"], ["client", "Client", "text"], ["amount", "Amount (₹)", "number"],
      ["status", "Status", "select", ["Draft", "Sent", "Paid", "Overdue"]], ["issue_date", "Issue date", "date"],
      ["due_date", "Due date", "date"], ["notes", "Notes", "text"]],
    defaults: { status: "Draft" },
  },
  expenses: {
    label: "Expenses", entity: "expense",
    columns: [["category", "Category"], ["vendor", "Vendor"], ["amount", "Amount", "money"], ["status", "Status", "badge"], ["date", "Date"]],
    fields: [["category", "Category", "text"], ["vendor", "Vendor", "text"], ["amount", "Amount (₹)", "number"],
      ["status", "Status", "select", ["Pending", "Paid"]], ["date", "Date", "date"], ["notes", "Notes", "text"]],
    defaults: { status: "Pending" },
  },
  budgets: {
    label: "Budgets", entity: "budget",
    columns: [["category", "Category"], ["period", "Period"], ["amount", "Allocated", "money"], ["notes", "Notes"]],
    fields: [["category", "Category", "text"], ["period", "Period", "text"], ["amount", "Allocated (₹)", "number"], ["notes", "Notes", "text"]],
    defaults: {},
  },
};

function statusKind(s) {
  return ["Paid", "Approved"].includes(s) ? "healthy" : s === "Overdue" ? "Overdue" : ["Sent", "Pending", "Draft"].includes(s) ? "pending" : "info";
}

function CrudTab({ tab, canCreate, canEdit }) {
  const cfg = CONFIG[tab];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/cc/finance/${tab}`).then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useRealtime(["finance"], load);

  const save = async () => {
    try {
      const payload = { ...editing };
      if (payload.amount != null) payload.amount = Number(payload.amount);
      if (payload.id) await api.put(`/cc/finance/${tab}/${payload.id}`, payload);
      else await api.post(`/cc/finance/${tab}`, payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this record?")) return; await api.delete(`/cc/finance/${tab}/${id}`); toast.success("Deleted"); load(); };

  return (
    <Panel testid={`finance-${tab}-panel`}
      action={canCreate && <CCButton onClick={() => setEditing({ ...cfg.defaults })} testid={`${cfg.entity}-add`}><Plus className="w-4 h-4" /> Add {cfg.entity}</CCButton>}>
      {loading ? <LoadingState /> : rows.length === 0 ? (
        <EmptyState title={`No ${cfg.label.toLowerCase()} yet`} hint={canCreate ? "Add the first record." : undefined} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
              {cfg.columns.map(([f, l]) => <th key={f} className="px-3 py-3 font-medium">{l}</th>)}
              {canEdit && <th className="px-3 py-3 text-right">Actions</th>}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-hairline/[0.05] text-ink" data-testid={`${cfg.entity}-row-${r.id}`}>
                  {cfg.columns.map(([f, l, kind]) => (
                    <td key={f} className="px-3 py-3">
                      {kind === "money" ? money(r[f]) : kind === "badge" ? <StatusBadge status={statusKind(r[f])} label={r[f]} /> : (r[f] || "—")}
                    </td>
                  ))}
                  {canEdit && <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setEditing({ ...r })} className="text-subtle hover:text-ink p-1.5" data-testid={`${cfg.entity}-edit-${r.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(r.id)} className="text-subtle hover:text-red-500 p-1.5" data-testid={`${cfg.entity}-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditing(null)} data-testid={`${cfg.entity}-modal`}>
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">{editing.id ? "Edit" : "Add"} {cfg.entity}</h3>
            <div className="grid grid-cols-2 gap-4">
              {cfg.fields.map(([f, l, type, opts]) => (
                <Field key={f} label={l}>
                  {type === "select" ? (
                    <select className={inputCls} value={editing[f] || opts[0]} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} data-testid={`${cfg.entity}-field-${f}`}>
                      {opts.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={type} className={inputCls} value={editing[f] ?? ""} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })} data-testid={`${cfg.entity}-field-${f}`} />
                  )}
                </Field>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button>
              <CCButton onClick={save} testid={`${cfg.entity}-save`}>Save</CCButton>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

export default function Finance() {
  const { can } = useCommand();
  const [tab, setTab] = useState("invoices");
  const [summary, setSummary] = useState(null);
  const canCreate = can("finance", "CREATE") || can("finance", "MANAGE");
  const canEdit = can("finance", "EDIT") || can("finance", "MANAGE");

  const load = useCallback(() => { api.get("/cc/finance/summary").then(({ data }) => setSummary(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["finance"], load);

  return (
    <div>
      <h1 className="display text-2xl md:text-3xl font-light text-ink">Finance</h1>
      <p className="text-subtle mt-1 mb-6">Revenue, expenses, invoices and budgets — live.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Revenue" value={summary?.revenue ?? 0} prefix="₹" empty={!summary?.configured} />
        <KpiCard label="Net Cash" value={summary?.net_cash ?? 0} prefix="₹" empty={!summary?.configured} />
        <KpiCard label="Outstanding" value={summary?.outstanding ?? 0} prefix="₹" empty={!summary?.configured} />
        <KpiCard label="Expenses" value={summary?.expenses ?? 0} prefix="₹" empty={!summary?.configured} />
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(CONFIG).map(([k, v]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`finance-tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm transition-colors ${tab === k ? "bg-invert text-invert-ink" : "glass-medium text-subtle hover:text-ink"}`}>{v.label}</button>
        ))}
      </div>

      <CrudTab tab={tab} canCreate={canCreate} canEdit={canCreate || canEdit} />
    </div>
  );
}
